import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { Exam, Question, Choice } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { ArrowLeft, Plus, Edit, Trash2, Save, X, CheckCircle } from 'lucide-react'

interface QuestionWithChoices extends Question {
  choices: Choice[]
  correctChoiceIds?: string[]
}

export function ExamQuestionsPage() {
  useDocumentTitle('試験問題管理')
  const { examId } = useParams<{ examId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTenant, memberships } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exam, setExam] = useState<Exam | null>(null)
  const [questions, setQuestions] = useState<QuestionWithChoices[]>([])
  const [editingQuestion, setEditingQuestion] = useState<QuestionWithChoices | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'single_choice' as 'single_choice' | 'multiple_choice' | 'text',
    points: 10,
    choices: [
      { choice_text: '', is_correct: false },
      { choice_text: '', is_correct: false },
    ],
    text_answer: '',
  })

  useEffect(() => {
    const isTenantAdmin = memberships.some(
      (m) => m.tenant_id === currentTenant?.tenant_id && m.role_in_tenant === 'tenant_admin'
    )

    if (!isTenantAdmin && memberships.length > 0) {
      navigate('/mypage', { replace: true })
      return
    }

    if (examId && user && currentTenant) {
      fetchExamAndQuestions()
    }
  }, [examId, user, currentTenant, memberships])

  const fetchExamAndQuestions = async () => {
    if (!examId || !currentTenant) return

    try {
      setLoading(true)
      setError(null)

      // 試験情報を取得
      const { data: examData, error: examError } = await supabase
        .from('exams')
        .select('*, courses(*)')
        .eq('exam_id', examId)
        .eq('tenant_id', currentTenant.tenant_id)
        .single()

      if (examError) throw examError
      setExam(examData)

      // 問題一覧を取得
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('*')
        .eq('exam_id', examId)
        .order('sort_order', { ascending: true })

      if (questionsError) throw questionsError

      // 各問題の選択肢と正解を取得
      const questionsWithDetails: QuestionWithChoices[] = []
      for (const question of questionsData || []) {
        // 選択肢取得
        const { data: choicesData } = await supabase
          .from('choices')
          .select('*')
          .eq('question_id', question.question_id)
          .order('sort_order', { ascending: true })

        // 正解取得
        const { data: answerKeysData } = await supabase
          .from('answer_keys')
          .select('*')
          .eq('question_id', question.question_id)
          .single()

        questionsWithDetails.push({
          ...question,
          choices: choicesData || [],
          correctChoiceIds: answerKeysData?.choice_ids || [],
        })
      }

      setQuestions(questionsWithDetails)
    } catch (err: any) {
      console.error('Error fetching exam and questions:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveQuestion = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!examId || !currentTenant || !exam) return

    try {
      const questionData = {
        exam_id: examId,
        tenant_id: currentTenant.tenant_id,
        question_text: formData.question_text,
        question_type: formData.question_type,
        points: formData.points,
        sort_order: editingQuestion ? editingQuestion.sort_order : questions.length + 1,
      }

      let questionId: string

      if (editingQuestion) {
        // 更新
        const { error: updateError } = await supabase
          .from('questions')
          .update(questionData)
          .eq('question_id', editingQuestion.question_id)

        if (updateError) throw updateError
        questionId = editingQuestion.question_id

        // 既存の選択肢と正解を削除
        await supabase.from('choices').delete().eq('question_id', questionId)
        await supabase.from('answer_keys').delete().eq('question_id', questionId)
      } else {
        // 新規作成
        const { data, error: insertError } = await supabase
          .from('questions')
          .insert([questionData])
          .select()
          .single()

        if (insertError) throw insertError
        questionId = data.question_id
      }

      // 選択肢を作成（選択式の場合）
      if (formData.question_type !== 'text') {
        const choicesData = formData.choices
          .filter((c) => c.choice_text.trim())
          .map((choice, index) => ({
            question_id: questionId,
            tenant_id: currentTenant.tenant_id,
            choice_text: choice.choice_text,
            sort_order: index + 1,
          }))

        const { data: insertedChoices, error: choicesError } = await supabase
          .from('choices')
          .insert(choicesData)
          .select()

        if (choicesError) throw choicesError

        // 正解を設定
        const correctChoiceIds = insertedChoices
          .filter((_, index) => formData.choices[index].is_correct)
          .map((c) => c.choice_id)

        if (correctChoiceIds.length > 0) {
          await supabase.from('answer_keys').insert([
            {
              question_id: questionId,
              tenant_id: currentTenant.tenant_id,
              choice_ids: correctChoiceIds,
            },
          ])
        }
      } else {
        // 記述式の正解を設定
        await supabase.from('answer_keys').insert([
          {
            question_id: questionId,
            tenant_id: currentTenant.tenant_id,
            correct_text: formData.text_answer || null,
          },
        ])
      }

      // リセット
      setFormData({
        question_text: '',
        question_type: 'single_choice',
        points: 10,
        choices: [
          { choice_text: '', is_correct: false },
          { choice_text: '', is_correct: false },
        ],
        text_answer: '',
      })
      setEditingQuestion(null)
      setShowForm(false)
      fetchExamAndQuestions()
    } catch (err: any) {
      console.error('Error saving question:', err)
      setError(err.message || '問題の保存に失敗しました')
    }
  }

  const handleEditQuestion = (question: QuestionWithChoices) => {
    setEditingQuestion(question)
    setFormData({
      question_text: question.question_text,
      question_type: question.question_type,
      points: question.points,
      choices:
        question.choices.length > 0
          ? question.choices.map((c) => ({
              choice_text: c.choice_text,
              is_correct: question.correctChoiceIds?.includes(c.choice_id) || false,
            }))
          : [
              { choice_text: '', is_correct: false },
              { choice_text: '', is_correct: false },
            ],
      text_answer: '',
    })
    setShowForm(true)
  }

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('この問題を削除してもよろしいですか？')) return

    try {
      // 関連データは CASCADE で自動削除される
      const { error: deleteError } = await supabase
        .from('questions')
        .delete()
        .eq('question_id', questionId)

      if (deleteError) throw deleteError

      fetchExamAndQuestions()
    } catch (err: any) {
      console.error('Error deleting question:', err)
      setError(err.message || '問題の削除に失敗しました')
    }
  }

  const addChoice = () => {
    setFormData({
      ...formData,
      choices: [...formData.choices, { choice_text: '', is_correct: false }],
    })
  }

  const removeChoice = (index: number) => {
    setFormData({
      ...formData,
      choices: formData.choices.filter((_, i) => i !== index),
    })
  }

  const updateChoice = (index: number, field: 'choice_text' | 'is_correct', value: any) => {
    const newChoices = [...formData.choices]
    newChoices[index] = { ...newChoices[index], [field]: value }

    // 単一選択の場合、他の選択肢のis_correctをfalseにする
    if (field === 'is_correct' && value && formData.question_type === 'single_choice') {
      newChoices.forEach((choice, i) => {
        if (i !== index) choice.is_correct = false
      })
    }

    setFormData({ ...formData, choices: newChoices })
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="試験を読み込み中..." />
      </MainLayout>
    )
  }

  if (!exam) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message="試験が見つかりませんでした" />
        </div>
      </MainLayout>
    )
  }

  const course = exam.courses as any

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/tenant/courses')}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-500"
          >
            <ArrowLeft className="h-4 w-4" />
            講座一覧へ戻る
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{exam.title}</h1>
          <p className="mt-2 text-gray-600">
            講座: {course?.title} | 制限時間: {exam.time_limit_minutes}分 | 合格点: {exam.passing_score}点
          </p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* 問題一覧 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              問題一覧（{questions.length}問）
            </h2>
            <button
              onClick={() => {
                setEditingQuestion(null)
                setFormData({
                  question_text: '',
                  question_type: 'single_choice',
                  points: 10,
                  choices: [
                    { choice_text: '', is_correct: false },
                    { choice_text: '', is_correct: false },
                  ],
                  text_answer: '',
                })
                setShowForm(true)
              }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              新規問題追加
            </button>
          </div>

          {questions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">まだ問題がありません</p>
          ) : (
            <div className="space-y-3">
              {questions.map((question, index) => (
                <div
                  key={question.question_id}
                  className="p-4 border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-medium text-gray-500">問{index + 1}</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {question.points}点
                        </span>
                        <span className="text-xs text-gray-600">
                          {question.question_type === 'single_choice' && '単一選択'}
                          {question.question_type === 'multiple_choice' && '複数選択'}
                          {question.question_type === 'text' && '記述式'}
                        </span>
                      </div>
                      <p className="text-gray-900 mb-2">{question.question_text}</p>
                      {question.choices.length > 0 && (
                        <div className="ml-4 space-y-1">
                          {question.choices.map((choice) => (
                            <div
                              key={choice.choice_id}
                              className="flex items-center gap-2 text-sm text-gray-600"
                            >
                              {question.correctChoiceIds?.includes(choice.choice_id) && (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              )}
                              <span>{choice.choice_text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEditQuestion(question)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question.question_id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 問題フォーム */}
        {showForm && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingQuestion ? '問題編集' : '新規問題'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false)
                  setEditingQuestion(null)
                }}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSaveQuestion} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  問題文 <span className="text-red-600">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    問題タイプ <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={formData.question_type}
                    onChange={(e) =>
                      setFormData({ ...formData, question_type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="single_choice">単一選択</option>
                    <option value="multiple_choice">複数選択</option>
                    <option value="text">記述式</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    配点 <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.points}
                    onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* 選択肢（選択式の場合） */}
              {formData.question_type !== 'text' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      選択肢 <span className="text-red-600">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={addChoice}
                      className="text-sm text-blue-600 hover:text-blue-500"
                    >
                      + 選択肢を追加
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.choices.map((choice, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type={formData.question_type === 'single_choice' ? 'radio' : 'checkbox'}
                          checked={choice.is_correct}
                          onChange={(e) => updateChoice(index, 'is_correct', e.target.checked)}
                          className="flex-shrink-0"
                        />
                        <input
                          type="text"
                          required
                          value={choice.choice_text}
                          onChange={(e) => updateChoice(index, 'choice_text', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={`選択肢${index + 1}`}
                        />
                        {formData.choices.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeChoice(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    正解にチェックを入れてください
                  </p>
                </div>
              )}

              {/* 記述式の模範解答 */}
              {formData.question_type === 'text' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    模範解答（任意）
                  </label>
                  <textarea
                    rows={3}
                    value={formData.text_answer}
                    onChange={(e) => setFormData({ ...formData, text_answer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="採点の参考となる模範解答"
                  />
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
                >
                  <Save className="h-5 w-5" />
                  保存
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false)
                    setEditingQuestion(null)
                  }}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
